import { useEffect, useRef } from 'react';

import {
    ORGANIZATION_EVENTS_RECONNECTED_EVENT,
    organizationEventBus,
    type OrganizationEventsReconnectedDetail,
} from '@apps/web/lib/event_bus';
import { ApiError } from '@libraries/typescript/api_client/error';

const INITIAL_RECOVERY_DELAY_MAX_MS = 5_000;
const BETWEEN_RECOVERY_MIN_DELAY_MS = 250;
const BETWEEN_RECOVERY_MAX_DELAY_MS = 750;

type RecoveryJob = {
    readonly label: string;
    readonly owner: symbol;
    readonly priority: number;
    readonly isActive: () => boolean;
    readonly run: () => Promise<void>;
};

class OrganizationRecoveryQueue {
    readonly pending = new Map<symbol, RecoveryJob>();
    private running = false;
    private timer: ReturnType<typeof setTimeout> | null = null;

    get isRunning(): boolean {
        return this.running;
    }

    get hasTimer(): boolean {
        return this.timer !== null;
    }

    start(): void {
        this.running = true;
    }

    finish(): void {
        this.running = false;
    }

    schedule(callback: () => void, delayMs: number): void {
        this.timer = setTimeout(() => {
            this.timer = null;
            callback();
        }, delayMs);
    }

    clearTimer(): void {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}

const queues = new Map<string, OrganizationRecoveryQueue>();
let visibilityListenerReferences = 0;

function isDocumentVisible(): boolean {
    return (
        typeof document === 'undefined' ||
        document.visibilityState === 'visible'
    );
}

function getQueue(organizationId: string): OrganizationRecoveryQueue {
    let queue = queues.get(organizationId);
    if (queue === undefined) {
        queue = new OrganizationRecoveryQueue();
        queues.set(organizationId, queue);
    }
    return queue;
}

function nextJob(queue: OrganizationRecoveryQueue): RecoveryJob | undefined {
    return [...queue.pending.values()].sort(
        (left, right) => left.priority - right.priority,
    )[0];
}

function scheduleDrain(
    organizationId: string,
    queue: OrganizationRecoveryQueue,
    delayMs: number,
): void {
    if (
        queue.isRunning ||
        queue.hasTimer ||
        queue.pending.size === 0 ||
        !isDocumentVisible()
    ) {
        return;
    }

    queue.schedule(() => {
        void drainQueue(organizationId, queue);
    }, delayMs);
}

async function drainQueue(
    organizationId: string,
    queue: OrganizationRecoveryQueue,
): Promise<void> {
    if (queue.isRunning || !isDocumentVisible()) {
        return;
    }

    const job = nextJob(queue);
    if (job === undefined) {
        queues.delete(organizationId);
        return;
    }

    queue.pending.delete(job.owner);
    queue.start();
    let retryDelayMs: number | null = null;
    try {
        await job.run();
    } catch (error) {
        if (
            error instanceof ApiError &&
            error.globalOverloadRetryAtMs !== undefined &&
            job.isActive()
        ) {
            queue.pending.set(job.owner, job);
            retryDelayMs =
                Math.max(0, error.globalOverloadRetryAtMs - Date.now()) +
                Math.random() * INITIAL_RECOVERY_DELAY_MAX_MS;
        } else {
            console.debug(
                `[Realtime recovery] ${job.label} failed; continuing the queue`,
                error,
            );
        }
    } finally {
        queue.finish();
    }
    if (queue.pending.size === 0) {
        queues.delete(organizationId);
        return;
    }

    const nextDelayMs =
        retryDelayMs ??
        BETWEEN_RECOVERY_MIN_DELAY_MS +
            Math.random() *
                (BETWEEN_RECOVERY_MAX_DELAY_MS - BETWEEN_RECOVERY_MIN_DELAY_MS);
    scheduleDrain(organizationId, queue, nextDelayMs);
}

function enqueueRecovery(organizationId: string, job: RecoveryJob): void {
    const queue = getQueue(organizationId);
    // A hook instance coalesces repeated reconnect events while preserving
    // distinct consumers that happen to share the same diagnostic label.
    queue.pending.set(job.owner, job);
    scheduleDrain(
        organizationId,
        queue,
        Math.random() * INITIAL_RECOVERY_DELAY_MAX_MS,
    );
}

function onVisibilityChange(): void {
    if (!isDocumentVisible()) {
        return;
    }
    for (const [organizationId, queue] of queues) {
        scheduleDrain(organizationId, queue, 0);
    }
}

function retainVisibilityListener(): () => void {
    if (typeof document === 'undefined') {
        return () => undefined;
    }
    if (visibilityListenerReferences === 0) {
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
    visibilityListenerReferences += 1;

    return () => {
        visibilityListenerReferences -= 1;
        if (visibilityListenerReferences === 0) {
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );
        }
    };
}

/**
 * Register one stable, deduplicated job for organization-event recovery.
 *
 * Jobs from a reconnect run one at a time per organization. Hidden documents
 * retain pending work without issuing requests and resume it when visible.
 */
export function useOrganizationReconnectRecovery({
    organizationId,
    recoveryLabel,
    priority,
    recover,
    enabled = true,
}: {
    readonly organizationId: string | null;
    readonly recoveryLabel: string;
    readonly priority: number;
    readonly recover: () => Promise<unknown> | unknown;
    readonly enabled?: boolean;
}): void {
    const ownerRef = useRef(Symbol(recoveryLabel));
    const recoverRef = useRef(recover);
    recoverRef.current = recover;

    useEffect(() => {
        if (organizationId === null || !enabled) {
            return;
        }

        const owner = ownerRef.current;
        let active = true;
        const releaseVisibilityListener = retainVisibilityListener();
        const onReconnect = (event: Event) => {
            const detail = (event as CustomEvent)
                .detail as OrganizationEventsReconnectedDetail;
            if (detail.organizationId !== organizationId) {
                return;
            }
            enqueueRecovery(organizationId, {
                label: recoveryLabel,
                owner,
                priority,
                isActive: () => active,
                run: async () => {
                    await recoverRef.current();
                },
            });
        };

        organizationEventBus.addEventListener(
            ORGANIZATION_EVENTS_RECONNECTED_EVENT,
            onReconnect,
        );
        return () => {
            active = false;
            organizationEventBus.removeEventListener(
                ORGANIZATION_EVENTS_RECONNECTED_EVENT,
                onReconnect,
            );
            releaseVisibilityListener();
            queues.get(organizationId)?.pending.delete(owner);
        };
    }, [enabled, organizationId, priority, recoveryLabel]);
}

/** Clear module-level queues between tests. */
export function resetOrganizationReconnectRecoveryForTests(): void {
    for (const queue of queues.values()) {
        queue.clearTimer();
    }
    queues.clear();
}
